import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter, Link } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  USER,
  LOGIN,
  PRODUCT_SUMMARY,
  EVENTS,
  PRODUCTS,
  ORDER_HISTORY,
  ACCOUNT
} from "../../../constants";
import eventType from "../../../prop-types/eventType";
import {
  fetchEventProducts,
  storeSelectedProduct
} from "../../../state/events/actions";
import EventProduct from "../components/event-product";
import EventPackage from "../components/event-package";
import "../styles/event-product.scss";
import { storeNextUrl } from "../../../state/user/actions";
import {
  Template,
  Divider,
  ButtonContainer,
  Button
} from "../../../commons/elements/index";

const propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    goBack: PropTypes.func.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      shortName: PropTypes.string.isRequired,
      eventId: PropTypes.string.isRequired
    })
  }).isRequired,
  // Map state to props
  types: PropTypes.arrayOf(eventType).isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      product_type: PropTypes.string,
      name: PropTypes.string,
      cost: PropTypes.number,
      isPackage: PropTypes.bool,
      isDayPass: PropTypes.bool,
      productList: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          startTime: PropTypes.string
        })
      )
    })
  ),
  loggedIn: PropTypes.bool.isRequired,
  tabName: PropTypes.string.isRequired,
  sepEnabled: PropTypes.bool.isRequired, // Single Event Purchasing
  // Map dispatch to props
  handleFetchEventProducts: PropTypes.func.isRequired,
  storeSelectedProductAction: PropTypes.func.isRequired,
  storeNextUrlAction: PropTypes.func.isRequired
};

const defaultProps = {
  products: null
};

class EventProducts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedProduct: {},
      selectedProductId: null
    };
  }

  componentDidMount() {
    const {
      handleFetchEventProducts,
      match: {
        params: { eventId }
      }
    } = this.props;
    handleFetchEventProducts(eventId);
  }

  goToLogin = () => {
    const {
      history,
      location,
      match: {
        params: { shortName }
      }
    } = this.props;
    history.push(`/${shortName}/${LOGIN}${location.search}`);
  };

  goToOrderSummary = () => {
    const {
      history,
      location,
      storeSelectedProductAction,
      storeNextUrlAction,
      loggedIn,
      match: {
        params: { shortName, eventId }
      }
    } = this.props;
    const { selectedProduct } = this.state;
    const nextUrl = `/${shortName}/${EVENTS}/${eventId}/${PRODUCTS}/${PRODUCT_SUMMARY}${location.search}`;
    storeSelectedProductAction(selectedProduct);
    if (loggedIn) {
      history.push(nextUrl);
    } else {
      storeNextUrlAction(nextUrl);
      this.goToLogin();
    }
  };

  selectProduct = (selectedProduct) => {
    this.setState({ selectedProduct });
  };

  showingDetails = (id) => {
    this.setState((prevState) => {
      if (prevState.selectedProductId === id) {
        return { selectedProductId: null };
      }
      return { selectedProductId: id };
    });
  };

  render() {
    const {
      types,
      products,
      history,
      location,
      match: {
        params: { shortName }
      },
      tabName,
      loggedIn,
      sepEnabled // Single Event Purchasing
    } = this.props;

    let eventProducts = [];
    let eventPackages = [];
    if (Array.isArray(products) && products.length) {
      eventProducts = products.filter((p) => !p.isPackage);
      eventPackages = products.filter((p) => p.isPackage);
    }

    const { selectedProduct, selectedProductId } = this.state;
    const { selectProduct, showingDetails, goToOrderSummary } = this;

    const orderHistoryLink = (
      <Link
        className="link-to-order-history py-3"
        to={`/${shortName}/${ACCOUNT}/${ORDER_HISTORY}${location.search}`}
      >
        Click here to view your order history
      </Link>
    );

    const signinLink = (
      <div className="py-3">
        Already purchased a product?
        <Link
          className="link-to-sign-in ml-2"
          to={`/${shortName}/${LOGIN}${location.search}`}
        >
          Sign In
        </Link>
      </div>
    );

    return (
      <Template
        header="Select a Product"
        tabName={USER}
        types={types}
        isLoading={!products}
      >
        <>
          {products && !!products.length && (
            <Formik
              initialValues={{
                productId:
                  (selectedProduct.id && selectedProduct.id.toString()) || null
              }}
              validationSchema={Yup.object({
                productId: Yup.string().required("Please select one product")
              })}
            >
              {() => (
                <Form className="event-product-form">
                  {loggedIn ? orderHistoryLink : signinLink}
                  <div>
                    <Divider />
                    {sepEnabled &&
                      eventProducts.map((product) => (
                        <div key={`event-product-${product.productId}`}>
                          <EventProduct
                            key={`event-product-${product.productId}`}
                            product={product}
                            selectProduct={selectProduct}
                          />
                          <Divider />
                        </div>
                      ))}
                    {eventPackages.map((pkg) => (
                      <div key={`event-package-${pkg.packageId}`}>
                        <EventPackage
                          key={`event-package-${pkg.packageId}`}
                          pkg={pkg}
                          selectPackage={selectProduct}
                          showingDetails={showingDetails}
                          expandedId={selectedProductId}
                        />
                        <Divider />
                      </div>
                    ))}
                  </div>
                  <ButtonContainer>
                    <Button
                      label="Cancel"
                      className="cancel-btn"
                      onClick={() =>
                        history.push(`/${shortName}/${EVENTS}/${tabName}`)
                      }
                    />
                    <Button
                      label="Continue"
                      disabled={!selectedProduct.id}
                      onClick={goToOrderSummary}
                    />
                  </ButtonContainer>
                </Form>
              )}
            </Formik>
          )}
          {products && !products.length && (
            <p>
              Oops! Looks like the pricing for this event has not been set up
              yet! Please email our client care team at{" "}
              <a href="mailto:stream@prestosports.com">
                stream@prestosports.com
              </a>{" "}
              and we will work with the school/organization to get the pricing
              set up as soon as possible!
            </p>
          )}
        </>
      </Template>
    );
  }
}

EventProducts.propTypes = propTypes;
EventProducts.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  types: state.events?.events?.types,
  products: state.events.events.products,
  loggedIn: state.user.loggedIn,
  tabName: state.filters.filters.tabName,
  sepEnabled: state.client.client.features.sepEnabled // Single Event Purchasing
});

const mapDispatchToProps = {
  handleFetchEventProducts: fetchEventProducts,
  storeSelectedProductAction: storeSelectedProduct,
  storeNextUrlAction: storeNextUrl
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(EventProducts);
